import { useState, Fragment } from "react";
import { useImagesBySignedUrl } from "../hooks/useImagesBySignedUrl";
import { Media } from "../types/media";
import {
  CarouselImage,
  PaddedContainer,
  ThumbnailImage,
} from "../styles/styles";
import { useImagesByMediaId } from "../hooks/useImagesByMediaId";
import FixaCarousel, {
  FixaOverflowCarousel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaCarousel/FixaCarousel";
import { DesktopImageViewer } from "./DesktopImageViewer";
import { MobileImageViewer } from "./MobileImageViewer";
import { DesktopOnly } from "@coworker/apprestructured/src/shared/components/DesktopOnly";
import { MobileOnly } from "@coworker/apprestructured/src/shared/components/MobileOnly";

interface MediaImagesCarouselProps {
  media: Media;
}

export const MediaImagesCarousel = ({ media }: MediaImagesCarouselProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [slidingIndex, setSlidingIndex] = useState(0);
  const { data: images } = useImagesByMediaId(media.id);

  if (!images || images.length === 0) {
    return <></>;
  }

  return (
    <>
      <PaddedContainer $py={"0px"} $px={"16px"}>
        <FixaCarousel
          id="imagesByMedia"
          data-testid="imagesByMedia"
          slideIndex={slidingIndex}
          hasSlideCounter={true}
          onVisibleSlideChange={(visibleIndices) => {
            if (visibleIndices.length > 0) {
              const indexed = visibleIndices[0] ?? 0;
              setSlidingIndex(indexed);
            }
          }}
        >
          {images.map((image, index) => {
            const imageProps = {
              src: image?.url ?? "",
              $rotation: image?.rotation || 0,
              $clickable: true as const,
              fallback: !image?.url || image.url.trim() === "",
              onClick: () => setOpenModal(true),
            };

            return (
              <Fragment key={image?.id ?? index}>
                <DesktopOnly>
                  <CarouselImage {...imageProps} $variant="desktop" />
                </DesktopOnly>
                <MobileOnly>
                  <CarouselImage {...imageProps} $variant="mobile" />
                </MobileOnly>
              </Fragment>
            );
          })}
        </FixaCarousel>
        <FixaOverflowCarousel
          id="thumbnailImagesByMedia"
          data-testid="thumbnailImagesByMedia"
          slideIndex={slidingIndex}
        >
          {images.map((image, index) => {
            const imageProps = {
              src: image?.url ?? "",
              $rotation: image?.rotation || 0,
              $selected: slidingIndex === index,
              onClick: () => setSlidingIndex(index),
            };

            return (
              <Fragment key={image?.id ?? index}>
                <ThumbnailImage {...imageProps} />
              </Fragment>
            );
          })}
        </FixaOverflowCarousel>
      </PaddedContainer>

      <DesktopOnly>
        {openModal && (
          <DesktopImageViewer
            images={images}
            isOpen={openModal}
            onClose={() => setOpenModal(false)}
            slideIndex={slidingIndex}
          />
        )}
      </DesktopOnly>

      <MobileOnly>
        {openModal && (
          <MobileImageViewer
            images={images}
            isOpen={openModal}
            onClose={() => setOpenModal(false)}
            slideIndex={slidingIndex}
          />
        )}
      </MobileOnly>
    </>
  );
};
